import React from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Laptop, Smartphone, CheckCircle, Clock, Shield, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  monitor: Monitor,
  laptop: Laptop,
  smartphone: Smartphone
};

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 to-transparent pointer-events-none" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-800/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-8">
            <div className="inline-block px-4 py-2 bg-blue-900/30 border border-blue-700/50 rounded-full">
              <span className="text-blue-400 text-sm font-medium">Assistência Técnica Especializada</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
              Soluções Técnicas em
              <span className="block mt-2 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                Computadores e Celulares
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Especialistas em manutenção e reparo de computadores, notebooks e smartphones em Campo Grande - MS. 
              Qualidade, agilidade e confiança no atendimento.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link
                to="/contato"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-xl shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center space-x-2 group"
              >
                <span>Solicitar Orçamento</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://wa.me/5567981556252?text=Entrei%20em%20contato%20por%20meio%20do%20seu%20website"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-slate-800 border border-blue-700/50 text-white rounded-lg font-semibold hover:bg-slate-700 transition-all duration-300"
              >
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white">
              Nossos Serviços
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Atendimento especializado para todos os seus dispositivos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group relative bg-slate-900 border border-blue-900/30 rounded-2xl p-8 hover:border-blue-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                  
                  <div className="relative z-10 space-y-4">
                    <div className="w-16 h-16 bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:bg-blue-800/40 transition-colors">
                      <IconComponent size={32} className="text-blue-400" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white group-hover:text-blue-400 transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Qualidade Garantida</h3>
              <p className="text-gray-400">
                Trabalhamos com peças originais e garantia em todos os serviços
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
                <Clock size={32} className="text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Atendimento Rápido</h3>
              <p className="text-gray-400">
                Diagnóstico ágil e reparo em tempo recorde
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
                <Shield size={32} className="text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Profissionais Qualificados</h3>
              <p className="text-gray-400">
                Equipe especializada e experiente no mercado
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-950/40 border border-blue-700/50 rounded-3xl p-12 text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Pronto para resolver seu problema?
            </h2>
            <p className="text-lg text-gray-300">
              Entre em contato agora e receba um orçamento sem compromisso
            </p>
            <Link
              to="/contato"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-xl shadow-blue-500/30 hover:shadow-blue-500/50 group"
            >
              <span>Solicitar Atendimento</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
